'use client';

import { Button } from '@zillow/constellation-10';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useListingCTAs } from '@/src/components/features/property/hooks';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import * as styles from './inlineCTAs.styles';

type InlineCTAsProps = {
  listing: PropertyDetailListing;
};

/**
 * Inline pair of bottom-of-summary CTAs (Message + Book tour). Mirrors the
 * sticky-header CTAs but full-width inside the summary card. Reads from
 * `useListingCTAs` so the same conditional gating applies (tour only if
 * the listing has instant-tour providers, etc.).
 */
export function InlineCTAs({ listing }: InlineCTAsProps) {
  const isDesktop = useIsDesktop();

  if (!isDesktop) return null;

  return <DesktopInlineCTAs listing={listing} />;
}

function DesktopInlineCTAs({ listing }: InlineCTAsProps) {
  const { ctas, overlays } = useListingCTAs(listing);

  if (ctas.length === 0) return null;

  return (
    <>
      <div className={styles.row} data-testid="inline-cta-buttons">
        {ctas.map((cta) => (
          <Button
            key={cta.id}
            type="button"
            tone="brand"
            emphasis={cta.emphasis}
            fluid
            icon={cta.icon}
            css={{ flex: 1 }}
            onClick={cta.onClick}
            disabled={cta.disabled}
            aria-busy={cta.ariaBusy}
            aria-label={cta.ariaLabel}
            data-testid={`button-${cta.id === 'tour' ? 'book-tour' : cta.id}`}
          >
            {cta.label}
          </Button>
        ))}
      </div>
      {overlays}
    </>
  );
}
